/**
 * Chrome 151 회귀 우회 — lucide 아이콘 스트로크 두께 보정.
 *
 * lucide 아이콘은 24 단위 viewBox 를 실제 렌더 크기(`w-4`, `size={20}` 등)로 줄여 그린다.
 * `stroke-width` 는 user unit 이라 아이콘이 작아지면 두께도 같이 얇아지고,
 * 그러면 Figma 에 지정된 두께와 화면에 보이는 두께가 달라진다.
 * `vector-effect: non-scaling-stroke`(global.css) 가 이 스케일을 무시하고
 * 두께를 CSS px 로 고정해서 그 불일치를 없애준다.
 *
 * 그런데 Chrome 151 은 이 두께를 CSS px 이 아니라 device px 으로 해석한다.
 * 그래서 레티나(DPR 2)에서 모든 아이콘 스트로크가 의도의 절반 두께로 그려진다.
 *
 * 여기서는 몇 배 얇아지는지 실측해 `--lucide-stroke-scale` 로 돌려준다.
 * 정상 브라우저는 1 이라 아무것도 바뀌지 않고, Chrome 이 버그를 고치면
 * 자동으로 1 이 되므로 두어도 무해하다. 그 시점에 이 파일과 global.css 의
 * `calc()` 를 함께 지우면 된다.
 *
 * 두께 값 자체는 CSS 가 소유한다. 이 모듈은 배수만 다룬다.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

/** 측정용 선의 두께. 이분탐색 오차 대비 충분히 굵게 잡는다. */
const PROBE_STROKE_WIDTH = 10;

/**
 * 배수 상한. 어긋난 배수는 devicePixelRatio 라서 현실적으로 3 을 넘지 않는다.
 * 측정이 틀어져 배수가 튀면 아이콘이 검은 덩어리가 되므로 그 전에 끊는다.
 */
const MAX_STROKE_SCALE = 4;

/**
 * 선 중앙에서 바깥으로 이분탐색해 실제로 칠해진 두께를 구한다.
 *
 * @returns user unit 두께. 선 위에서조차 히트되지 않으면 0.
 */
function measureStrokeThickness(path: SVGPathElement): number {
  if (!path.isPointInStroke(new DOMPoint(50, 50))) return 0;

  let inside = 0;
  let outside = PROBE_STROKE_WIDTH * 4;

  for (let i = 0; i < 30; i++) {
    const middle = (inside + outside) / 2;
    if (path.isPointInStroke(new DOMPoint(50, 50 + middle))) {
      inside = middle;
    } else {
      outside = middle;
    }
  }

  return inside * 2;
}

/**
 * non-scaling-stroke 가 몇 배 얇게 그려지는지 측정한다.
 *
 * viewBox 스케일을 1 로 맞춰두면 정상 브라우저는 지정한 두께 그대로 그린다.
 * 측정값이 그보다 작으면 그 비율이 곧 어긋난 배수다.
 *
 * @returns 정상이면 1, Chrome 151 이면 devicePixelRatio
 */
function measureStrokeShrinkFactor(): number {
  // 이 함수는 render() 보다 먼저 톱레벨에서 돈다. 여기서 던지면 앱이 아예 안 뜨므로
  // DOM 조작까지 전부 try 안에 둔다. 아이콘 두께 보정이 부팅을 막아선 안 된다.
  let svg: SVGSVGElement | undefined;

  try {
    svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "100");
    svg.setAttribute("height", "100");
    svg.setAttribute("viewBox", "0 0 100 100");
    svg.setAttribute("fill", "none");
    svg.style.cssText =
      "position:absolute;left:-9999px;top:0;opacity:0;pointer-events:none";

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", "M0 50 H100");
    path.style.strokeWidth = `${PROBE_STROKE_WIDTH}px`;
    path.style.vectorEffect = "non-scaling-stroke";
    svg.appendChild(path);
    document.body.appendChild(svg);

    const thickness = measureStrokeThickness(path);
    if (thickness <= 0) return 1;

    const factor = PROBE_STROKE_WIDTH / thickness;

    // 측정이 어긋나 배수가 튀면 아이콘이 뭉개진다.
    // 실패했을 때 안전한 쪽은 폭주가 아니라 "보정하지 않음"이다.
    if (!Number.isFinite(factor) || factor < 1 || factor > MAX_STROKE_SCALE) {
      return 1;
    }

    return factor;
  } catch {
    // isPointInStroke 를 쓸 수 없는 브라우저 — 보정하지 않는다(기존 동작 유지).
    return 1;
  } finally {
    svg?.remove();
  }
}

function applyStrokeScale(): void {
  document.documentElement.style.setProperty(
    "--lucide-stroke-scale",
    String(measureStrokeShrinkFactor()),
  );
}

/**
 * devicePixelRatio 가 바뀔 때마다 콜백을 부른다.
 *
 * resolution 미디어쿼리는 "현재 DPR" 조건이 깨질 때 한 번 발화하므로,
 * 발화 후 새 DPR 로 다시 걸어야 다음 변화를 잡을 수 있다.
 */
function watchDevicePixelRatio(onChange: () => void): void {
  const query = window.matchMedia(
    `(resolution: ${window.devicePixelRatio}dppx)`,
  );

  // 현재 DPR 로 쿼리를 걸지 못하면 change 가 영영 오지 않는다.
  // 감시가 아무 신호 없이 끊기지 않도록 resize 로 넘긴다.
  if (!query.matches) {
    watchDevicePixelRatioByResize(onChange);
    return;
  }

  query.addEventListener(
    "change",
    () => {
      onChange();
      watchDevicePixelRatio(onChange);
    },
    { once: true },
  );
}

/**
 * matchMedia 대체 경로. resize 는 줌·모니터 이동 양쪽에서 발화한다.
 * 이 경로로 들어오면 재무장 체인이 여기서 끝나므로 리스너는 하나만 남는다.
 */
function watchDevicePixelRatioByResize(onChange: () => void): void {
  let lastRatio = window.devicePixelRatio;

  window.addEventListener("resize", () => {
    if (window.devicePixelRatio === lastRatio) return;

    lastRatio = window.devicePixelRatio;
    onChange();
  });
}

/** 보정을 적용하고, 브라우저 줌·모니터 이동으로 DPR 이 바뀌면 다시 적용한다. */
export function startLucideStrokeScale(): void {
  applyStrokeScale();
  watchDevicePixelRatio(applyStrokeScale);
}
